import { useEffect, useState, type ReactNode } from "react";

import { ACCENT, AMD_COLOR } from "../components/chartTheme";
import { Button, Card, PageBody, PageHeader, cx } from "../components/ui";
import { ApiError, api } from "../lib/api";
import { FALLBACK_COUNTRIES, countryByIso } from "../lib/country";
import { formatDuration } from "../lib/format";
import { sipDisplayName } from "../lib/sipLabels";
import {
  parseOperatorDesk,
  type DeskCounts,
  type DeskCountry,
  type DeskOutcome,
  type DeskOtp,
  type DeskSeriesPoint,
  type DeskTrunk,
  type DeskWindowId,
  type OperatorDesk,
} from "../lib/stats";

const OUTCOME_ORDER = [
  "answered-human",
  "screening",
  "vm",
  "no-connect",
  "operator-hangup",
  "error",
] as const;

const OUTCOME_LABEL: Record<string, string> = {
  "answered-human": "Human",
  human: "Human",
  screening: "Screening",
  vm: "VM",
  machine: "VM",
  voicemail: "VM",
  "no-connect": "No connect",
  "operator-hangup": "Operator hangup",
  error: "Error",
};

const OTP_STEPS: Array<{ key: keyof DeskOtp; label: string; color: string }> = [
  { key: "prompted", label: "Prompted", color: "#6f7782" },
  { key: "sent", label: "Sent", color: ACCENT },
  { key: "collected", label: "Collecting", color: "#d8a25c" },
  { key: "match", label: "Match", color: AMD_COLOR.HUMAN },
  { key: "mismatch", label: "Mismatch", color: AMD_COLOR.MACHINE },
  { key: "timeout", label: "Timeout", color: AMD_COLOR.SCREENING },
];

function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const n = Math.round(value);
  const sign = n < 0 ? "-" : "";
  return sign + Math.abs(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatAsr(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return "—";
  const pct = rate > 1 || rate < -1 ? rate : rate * 100;
  return `${pct.toFixed(1)}%`;
}

function formatHandle(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return "—";
  return formatDuration(ms);
}

function shortDay(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return iso.slice(0, 8);
  return `${match[3]}/${match[2]}`;
}

function countryLabel(iso: string): string {
  const code = iso.trim().toUpperCase();
  if (code === "UNKNOWN") return "unknown";
  if (!/^[A-Z]{2}$/.test(code)) return "—";
  const known = countryByIso(code, FALLBACK_COUNTRIES);
  return known ? `${code} · ${known.name}` : code;
}

function trunkName(id: string): { name: string; sofia: string | null } {
  const raw = id.trim();
  if (!raw || raw.toLowerCase() === "unknown") return { name: "unknown", sofia: null };
  if (/^\+?\d{6,}$/.test(raw)) return { name: "—", sofia: null };
  const name = sipDisplayName(raw);
  return { name, sofia: name !== raw ? raw : null };
}

function outcomeLabel(outcome: string): string {
  const key = outcome.trim().toLowerCase();
  return OUTCOME_LABEL[key] ?? outcome.trim();
}

function outcomeColor(outcome: string): string {
  const key = outcome.trim().toLowerCase();
  if (key === "answered-human" || key === "human") return AMD_COLOR.HUMAN;
  if (key === "vm" || key === "machine" || key === "voicemail") return AMD_COLOR.MACHINE;
  if (key === "screening") return AMD_COLOR.SCREENING;
  if (key === "error") return AMD_COLOR.MACHINE;
  if (key === "operator-hangup") return ACCENT;
  return "#6f7782";
}

function orderOutcomes(rows: DeskOutcome[]): DeskOutcome[] {
  const rest = [...rows];
  const ordered: DeskOutcome[] = [];
  for (const key of OUTCOME_ORDER) {
    const index = rest.findIndex((row) => row.outcome.trim().toLowerCase() === key);
    if (index < 0) continue;
    const [row] = rest.splice(index, 1);
    if (row) ordered.push(row);
  }
  return [...ordered, ...rest];
}

function seriesHasVolume(points: DeskSeriesPoint[]): boolean {
  return points.some((point) => (point.calls ?? 0) > 0 || (point.human ?? 0) > 0);
}

function dayTitle(point: DeskSeriesPoint): string {
  const bits = [point.date];
  if (point.calls !== null) bits.push(`${formatCount(point.calls)} calls`);
  if (point.human !== null) bits.push(`${formatCount(point.human)} human`);
  if (point.machine !== null) bits.push(`${formatCount(point.machine)} VM`);
  if (point.screening !== null) bits.push(`${formatCount(point.screening)} screening`);
  if (point.asr !== null) bits.push(`ASR ${formatAsr(point.asr)}`);
  return bits.join(" · ");
}

function missingNote(desk: OperatorDesk): string | null {
  const missing: string[] = [];
  if (!desk.present.countries) missing.push("country");
  if (!desk.present.outcomes) missing.push("outcomes");
  if (!desk.present.otp) missing.push("OTP funnel");
  if (!desk.present.amdMix) missing.push("7d/30d AMD");
  if (!missing.length) return null;
  return `${missing.join(", ")} not in this payload — left blank. Nothing here is guessed.`;
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card className="px-4 py-3">
      <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">{label}</div>
      <div className="mt-1 font-mono text-2xl tabular-nums tracking-tight text-ink">{value}</div>
    </Card>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <Card className="min-w-0 px-5 py-4">
      <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">{title}</div>
      {hint ? <p className="mt-1 text-xs leading-relaxed text-mute">{hint}</p> : null}
      <div className="mt-3">{children}</div>
    </Card>
  );
}

function Blank() {
  return <span className="text-mute">—</span>;
}

function DaySeries({ points }: { points: DeskSeriesPoint[] }) {
  const max = Math.max(...points.map((point) => Math.max(point.calls ?? 0, point.human ?? 0)), 1);
  const dense = points.length > 16;
  const track = 112;
  return (
    <div className="overflow-x-auto">
      <div
        className="flex items-end gap-1"
        style={{ minWidth: points.length * (dense ? 28 : 44) }}
        role="img"
        aria-label={points.map(dayTitle).join(". ")}
      >
        {points.map((point, index) => {
          const calls = point.calls ?? 0;
          const human = point.human ?? 0;
          const callPx = point.calls === null || calls <= 0 ? 0 : Math.max(3, Math.round((calls / max) * track));
          const humanPx = point.human === null || human <= 0 ? 0 : Math.max(3, Math.round((human / max) * track));
          const asr = point.calls ? formatAsr(point.asr) : "";
          return (
            <div key={`${point.date}-${index}`} className="flex min-w-0 flex-1 flex-col items-center gap-1" title={dayTitle(point)}>
              <div className="h-4 font-mono text-[10px] tabular-nums text-ink-2">{asr === "—" ? "" : dense ? asr.replace("%", "") : asr}</div>
              <div className="flex h-28 items-end justify-center gap-0.5">
                <div className="w-2 rounded-sm bg-accent/80" style={{ height: callPx }} />
                <div className="w-2 rounded-sm" style={{ height: humanPx, background: AMD_COLOR.HUMAN }} />
              </div>
              <div className="font-mono text-[10px] tabular-nums text-mute">
                {dense && index % 5 !== 0 && index !== points.length - 1 ? "" : shortDay(point.date)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SplitTable({
  kind,
  trunks,
  countries,
}: {
  kind: "trunk" | "country";
  trunks?: DeskTrunk[];
  countries?: DeskCountry[];
}) {
  const trunkRows = trunks ?? [];
  const countryRows = countries ?? [];
  const empty = kind === "trunk" ? trunkRows.length === 0 : countryRows.length === 0;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-[11px] uppercase tracking-[0.14em] text-mute">
          <tr>
            <th className="py-2 pr-3 font-medium">{kind === "trunk" ? "Trunk" : "Country"}</th>
            <th className="py-2 pr-3 font-medium">Calls</th>
            <th className="py-2 pr-3 font-medium">Human</th>
            <th className="py-2 pr-3 font-medium">VM</th>
            <th className="py-2 pr-3 font-medium">Screen</th>
            <th className="py-2 pr-3 font-medium">ASR</th>
            {kind === "trunk" ? <th className="py-2 pr-3 font-medium">Handle</th> : null}
          </tr>
        </thead>
        <tbody>
          {empty ? (
            <tr>
              <td colSpan={kind === "trunk" ? 7 : 6} className="py-3 text-mute">
                —
              </td>
            </tr>
          ) : kind === "trunk" ? (
            trunkRows.map((row) => {
              const label = trunkName(row.trunk_id);
              return (
                <tr key={row.trunk_id} className="border-t border-white/5">
                  <td className="py-2 pr-3 text-ink">
                    {label.name}
                    {label.sofia ? <span className="ml-2 font-mono text-xs text-mute">{label.sofia}</span> : null}
                  </td>
                  <td className="py-2 pr-3 font-mono tabular-nums">{formatCount(row.calls)}</td>
                  <td className="py-2 pr-3 font-mono tabular-nums">{formatCount(row.human)}</td>
                  <td className="py-2 pr-3 font-mono tabular-nums">{formatCount(row.machine)}</td>
                  <td className="py-2 pr-3 font-mono tabular-nums">{formatCount(row.screening)}</td>
                  <td className="py-2 pr-3 font-mono tabular-nums">{formatAsr(row.asr)}</td>
                  <td className="py-2 pr-3 font-mono tabular-nums">{formatHandle(row.avg_duration_ms)}</td>
                </tr>
              );
            })
          ) : (
            countryRows.map((row) => (
              <tr key={row.country_iso2} className="border-t border-white/5">
                <td className="py-2 pr-3 text-ink">{countryLabel(row.country_iso2)}</td>
                <td className="py-2 pr-3 font-mono tabular-nums">{formatCount(row.calls)}</td>
                <td className="py-2 pr-3 font-mono tabular-nums">{formatCount(row.human)}</td>
                <td className="py-2 pr-3 font-mono tabular-nums">{formatCount(row.machine)}</td>
                <td className="py-2 pr-3 font-mono tabular-nums">{formatCount(row.screening)}</td>
                <td className="py-2 pr-3 font-mono tabular-nums">{formatAsr(row.asr)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function MeterList({
  rows,
}: {
  rows: Array<{ key: string; label: string; value: number | null; color: string }>;
}) {
  const known = rows.map((row) => row.value).filter((value): value is number => value !== null && value > 0);
  const max = known.length ? Math.max(...known) : 0;
  if (!rows.length) return <Blank />;
  return (
    <ul className="space-y-2">
      {rows.map((row) => {
        const width = row.value === null || max <= 0 ? 0 : Math.min(100, (Math.max(row.value, 0) / max) * 100);
        return (
          <li key={row.key} className="grid grid-cols-[7.5rem_minmax(0,1fr)_3.2rem] items-center gap-3">
            <span className="truncate text-sm text-ink-2">{row.label}</span>
            <span className="h-1.5 overflow-hidden rounded-full bg-inset">
              {row.value !== null && row.value > 0 ? (
                <span className="block h-1.5 rounded-full" style={{ width: `${width}%`, background: row.color }} />
              ) : null}
            </span>
            <span className="text-right font-mono text-sm tabular-nums text-ink">{formatCount(row.value)}</span>
          </li>
        );
      })}
    </ul>
  );
}

function AmdWindow({ label, counts }: { label: string; counts: DeskCounts | null }) {
  const parts = [
    { key: "Human", n: counts?.human ?? null, color: AMD_COLOR.HUMAN },
    { key: "VM", n: counts?.machine ?? null, color: AMD_COLOR.MACHINE },
    { key: "Screen", n: counts?.screening ?? null, color: AMD_COLOR.SCREENING },
    { key: "Unknown", n: counts?.unknown ?? null, color: AMD_COLOR.UNKNOWN },
  ];
  const sum = parts.reduce((total, part) => total + (part.n !== null && part.n > 0 ? part.n : 0), 0);
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-mute">{label}</div>
      {counts ? (
        <>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 font-mono text-xs tabular-nums text-ink">
            {parts.map((part) => (
              <span key={part.key} className="inline-flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: part.color }} />
                {part.key} {formatCount(part.n)}
              </span>
            ))}
          </div>
          {sum > 0 ? (
            <div className="mt-2 flex h-1.5 overflow-hidden rounded-full bg-inset" aria-hidden>
              {parts.map((part) =>
                part.n !== null && part.n > 0 ? (
                  <span key={part.key} style={{ width: `${(part.n / sum) * 100}%`, background: part.color }} />
                ) : null,
              )}
            </div>
          ) : null}
        </>
      ) : (
        <div className="mt-2 text-sm text-mute">—</div>
      )}
    </div>
  );
}

/**
 * Operator stats desk. One KPI strip, one day series, trunk/country tables,
 * outcome + OTP bars, and a small AMD mix. No pie charts. No dest/CLI/OTP digits.
 */
const WINDOW_CHOICES: Array<{ id: DeskWindowId; label: string }> = [
  { id: "today", label: "Today" },
  { id: "d7", label: "7 days" },
  { id: "d30", label: "30 days" },
];

export function Stats() {
  const [desk, setDesk] = useState<OperatorDesk | null>(null);
  const [windowId, setWindowId] = useState<DeskWindowId>("d30");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const body = await api.stats();
        if (cancelled) return;
        setDesk(parseOperatorDesk(body));
        setError(body == null ? "Stats API is not available" : null);
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof ApiError ? err.detail : "Stats unavailable";
        setError(message || "Stats unavailable");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    const timer = window.setInterval(() => void run(), 30000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [tick]);

  const summary = (desk && desk.windows[windowId]) || desk?.summary;
  const note = desk ? missingNote(desk) : null;
  const series = desk?.series ?? [];
  const showSeries = seriesHasVolume(series);
  const outcomes = orderOutcomes(desk?.outcomes ?? []);
  const otp = desk?.otp;

  return (
    <PageBody>
      <PageHeader
        kicker="Desk"
        title="Stats"
        actions={
          <Button variant="ghost" onClick={() => setTick((n) => n + 1)} disabled={loading && !desk}>
            Refresh
          </Button>
        }
      />
      <p className="mb-5 max-w-3xl text-sm text-mute">
        ASR is human connects ÷ calls. VM stays separate. AMD mix is stored amd_result, not the connect count. No destinations, CLIs, or OTP digits.
      </p>
      {error ? <p className="mb-4 text-sm text-fail">{error}</p> : null}
      {loading && !desk ? <p className="text-sm text-mute">Loading stats…</p> : null}
      {desk && summary ? (
        <div className="space-y-4">
          {desk.windows.today || desk.windows.d7 || desk.windows.d30 ? (
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="KPI window">
              {WINDOW_CHOICES.map((choice) => (
                <button
                  key={choice.id}
                  type="button"
                  disabled={!desk.windows[choice.id]}
                  className={cx(
                    "rounded-full px-3 py-1 text-xs disabled:cursor-not-allowed disabled:text-mute/40",
                    windowId === choice.id ? "bg-accent-soft text-accent" : "text-mute hover:text-ink",
                  )}
                  onClick={() => setWindowId(choice.id)}
                >
                  {choice.label}
                </button>
              ))}
              <span className="text-xs text-mute">KPI only. Series and tables stay on 30 days.</span>
            </div>
          ) : null}
          {desk.legacyWindow ? (
            <p className="text-xs text-mute">KPI is the loaded stats window, not a separate today / 7d / 30d summary.</p>
          ) : null}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7">
            <Kpi label="Calls" value={formatCount(summary.calls)} />
            <Kpi label="Human" value={formatCount(summary.human)} />
            <Kpi label="ASR" value={formatAsr(summary.asr)} />
            <Kpi label="VM" value={formatCount(summary.machine)} />
            <Kpi label="Screening" value={formatCount(summary.screening)} />
            <Kpi label="OTP verified" value={formatCount(summary.otp_verified)} />
            <Kpi label="Avg handle" value={formatHandle(summary.avg_handle_ms)} />
          </div>

          <Section
            title="Day series"
            hint={
              showSeries
                ? `Calls and human, last ${series.length} day${series.length === 1 ? "" : "s"}. Number above the bars is ASR.`
                : undefined
            }
          >
            {showSeries ? (
              <>
                <div className="mb-3 flex gap-4 text-xs text-mute">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-sm bg-accent/80" />
                    Calls
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-sm" style={{ background: AMD_COLOR.HUMAN }} />
                    Human
                  </span>
                </div>
                <DaySeries points={series} />
              </>
            ) : (
              <p className="text-sm text-mute">{desk.present.series ? "No calls in this window" : "—"}</p>
            )}
          </Section>

          <div className="grid gap-4 lg:grid-cols-2">
            <Section title="Trunk" hint="Sorted by calls. Sofia id stays next to the display name.">
              <SplitTable kind="trunk" trunks={desk.byTrunk} />
            </Section>
            <Section title="Country" hint="ISO country only. Sorted by calls.">
              <SplitTable kind="country" countries={desk.byCountry} />
            </Section>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Section title="Outcome" hint="How the call ended. Not a second AMD chart.">
              <MeterList
                rows={outcomes.map((row) => ({
                  key: row.outcome,
                  label: outcomeLabel(row.outcome),
                  value: row.count,
                  color: outcomeColor(row.outcome),
                }))}
              />
            </Section>
            <Section title="OTP funnel" hint="Counts only. Digits never appear here.">
              <MeterList
                rows={OTP_STEPS.map((step) => ({
                  key: step.key,
                  label: step.label,
                  value: otp ? otp[step.key] : null,
                  color: step.color,
                }))}
              />
            </Section>
          </div>

          <Section title="AMD" hint="Stored amd_result. Today, 7 days, 30 days — not a second desk.">
            <div className="grid gap-4 md:grid-cols-3">
              <AmdWindow label="Today" counts={desk.amd.today} />
              <AmdWindow label="7 days" counts={desk.amd.d7} />
              <AmdWindow label="30 days" counts={desk.amd.d30} />
            </div>
          </Section>

          {note ? <p className="text-xs text-mute">{note}</p> : null}
        </div>
      ) : null}
    </PageBody>
  );
}
